<script>
	import { onMount, afterUpdate, createEventDispatcher } from 'svelte'
	import { Link, navigateTo } from 'yrv'

	import axios from 'axios'
	import moment from 'moment'
	import wait from '../../libs/wait'

	import FadeinFX from '../../components/FX/FadeinFx.svelte'
	import AdminLayout from '../../layouts/AdminLayout.svelte'
	import Sidepanel from '../../components/Sidepanel/Sidepanel.svelte'
	import ClientDashSideContent from './ClientDashSideContent.svelte'
	import LogsRenderer from '../Logs/LogsRenderer/LogsRenderer.svelte'
	import BasicSpinner from '../../components/Loading/BasicSpinner.svelte'
	import CreateLog from './CreateLog.svelte'

	// import { background } from '../../stores/app'
	import { background, notifySuccess, notifyError } from '../../components/Notifications/AppNotifications.svelte'
	import userStore from '../../stores/user'
	import storage from '../../libs/storage'
	import { periodStart, periodEnd, searchQuery, typeFilter } from '../../stores/clients'

	export let id

	const dispatch = createEventDispatcher()

	let screenReady = false,
		createLogFlow = false,
		model = null,
		fetchTimer = null,
		sectors = {},
		logs = []

	const interpolateList = (logsList) => {
		let result = [],
			date_s = '',
			today_s = moment().format('DD MMM YYYY')

		logsList.forEach((it) => {
			let ds = moment(it.date).format('DD MMM YYYY')

			if (ds !== date_s) {
				const isToday = today_s === ds
				date_s = ds
				result.push({
					id: `date-${ds}`,
					date: it.date,
					text: ds,
					_type: 'day-item',
					isToday
				})
			}

			it._type = 'program-item'
			it.sector = sectors[it.category]
			result.push(it)
		})

		return result
	}

	const initDateInterval = () => {
		let now = moment()
		periodEnd.set(moment().add(10, 'days').valueOf())
		if (now.date() > 15) periodStart.set(now.startOf('month').valueOf())
		else periodStart.set(now.startOf('month').subtract(1, 'month').valueOf())
	}

	const onPeriodChange = (event) => {
		const { start, end } = event.detail
		// console.log (start, end)
		periodStart.set(start)
		periodEnd.set(end)
		fetchLogs()
	}

	const onTypeChange = (event) => {
		const type = event.detail
		// console.log ('onType', type)
		typeFilter.set(type)
		fetchLogs()
	}

	const onSearch = (event) => {
		searchQuery.set(event.detail)
		fetchLogs()
	}

	const fetchModel = () => {
		if ($periodStart === 0) initDateInterval()

		background(true)
		axios
			.get('/api/clients/view/' + id)
			.then((result) => {
				model = result.data.client
				background(false)
				screenReady = true
				fetchLogs()
			})
			.catch((e) => {
				dispatch('app-event', { id: 'app-error', error: e })
				screenReady = true
			})
	}

	const fetchLogs = () => {
		return new Promise((resolve, reject) => {
			if (fetchTimer) {
				clearTimeout(fetchTimer)
				fetchTimer = null
			}

			fetchTimer = setTimeout(() => {
				background(true)
				axios
					.request({
						method: 'post',
						url: '/api/logs/history/' + id,
						params: {
							start: $periodStart,
							end: $periodEnd,
							query: $searchQuery,
							type: $typeFilter
						}
					})
					.then((result) => {
						sectors = result.data.sectors
						logs = interpolateList(result.data.logs)
						background(false)
						screenReady = true
						resolve()
					})
					.catch((e) => {
						dispatch('app-event', { id: 'app-error', error: e })
						reject()
					})
			}, 50)
		})
	}

	const loadMoreBefore = () => {
		let req_end = moment($periodStart).subtract(1, 'day').valueOf(),
			req_start = moment($periodStart).subtract(1, 'month').valueOf()

		periodStart.set(req_start)
		background(true)
		axios
			.request({
				method: 'post',
				url: '/api/logs/history/' + id,
				params: {
					start: req_start,
					end: req_end,
					query: $searchQuery,
					type: $typeFilter
				}
			})
			.then((result) => {
				logs = logs.concat(interpolateList(result.data.logs))

				background(false)
			})
			.catch((e) => {
				dispatch('app-event', { id: 'app-error', error: e })
			})
	}

	const onLogUpdate = async (event) => {
		// console.log ('onLogUpdate', event.detail);
		try {
			background(true)
			const { id: lid, program, description, notes, type, resolver } = event.detail
			const logItem = logs.find((it) => it.id === lid)
			await axios.request({
				method: 'post',
				url: '/api/clients/updateLogGroup',
				params: {
					pid: program,
					description,
					notes,
					type
				}
			})
			if (logItem) {
				logItem.type = type
				logItem.notes = notes
				logItem.description = description
				logs = logs
			}
			notifySuccess('Log aggiornato.')
			background(false)
			removeModifiers()
			if (typeof resolver === 'function') {
				resolver()
			}
		} catch (error) {
			notifyError('Errore nel tentativo di aggiornare il log.')
		}
	}

	const onTimeLogCreate = async (event) => {
		try {
			background(true)
			const { resolver, ...params } = event.detail
			// console.log ('onTimeLogCreate', params);
			await axios.request({
				method: 'post',
				url: '/api/clients/saveLogToClient',
				params
			})
			await fetchLogs()
			notifySuccess('Log aggiornato.')
			background(false)
			removeModifiers()
			if (typeof resolver === 'function') {
				resolver()
			}
		} catch (error) {
			notifyError('Errore nel tentativo di aggiornare il log.')
		}
	}

	const onTimeLogUpdate = (event) => {
		const { id: lid, time, notes, type, resolver } = event.detail
		const logItem = logs.find((it) => it.id === lid)
		background(true)
		axios
			.request({
				method: 'post',
				url: '/api/clients/updateLogToClient',
				params: {
					client: id,
					lid,
					time,
					type,
					notes
				}
			})
			.then(() => {
				if (logItem) {
					logItem.time = time
					logItem.notes = notes
					logs = logs
				}
				notifySuccess('Log aggiornato.')
				background(false)
				removeModifiers()
				if (typeof resolver === 'function') {
					resolver()
				}
			})
			.catch(() => notifyError('Errore nel tentativo di aggiornare il log.'))
	}

	const onLogDelete = (event) => {
		const { id } = event.detail
		// console.log ('logs', logs, logs.indexOf (logs.find (it => it.id === id)))

		axios
			.request({
				method: 'post',
				url: '/api/clients/deleteLogItem',
				params: {
					id
				}
			})
			.then(() => {
				fetchModel()
				notifySuccess('Log eliminato.')
				removeModifiers()
			})
	}

	function onLogCancel({ detail }) {
		const { id } = detail
		if (id.startsWith('temp-')) {
			logs.splice(
				logs.findIndex((it) => it.id === id),
				1
			)
			logs = logs
		}
		removeModifiers()
	}

	function onCreateLog() {
		createLogFlow = true
	}

	function onCancelCreateLog() {
		createLogFlow = false
	}

	function removeModifiers() {
		logs.filter((it) => it.editing).forEach((it) => delete it.editing)
	}

	function onConfirmCreateLog(event) {
		createLogFlow = false
		const { detail } = event
		const log = {
			...detail,
			client: id,
			editing: true,
			custom_notes: '',
			siblings: [],
			program: detail.id,
			sector: sectors[detail.category],
			time: null,
			user: null,
			id: 'temp-' + Math.random()
		}
		const existingLog = logs.find((it) => it.program === log.program)

		if (existingLog) {
			existingLog.editing = true
			logs = logs
			document.querySelector('[data-list-id="' + existingLog.id + '"]')?.scrollIntoView()
			return
		}

		logs = interpolateList([log])
			.concat(logs)
			.sort((a, b) => b.date - a.date)
	}

	function calculateTimeLogTotal(item, isOvertime = false) {
		let total = 0
		if (item.time) {
			if (isOvertime && item.type === 1) total += item.time
			else if (!isOvertime && item.type === 0) total += item.time

			item.siblings.forEach((it) => {
				if (isOvertime && it.type === 1) total += it.time
				else if (!isOvertime && it.type === 0) total += it.time
			})
		}
		return total
	}

	function calculateTotalTime(logs = [], isOvertime = false) {
		let total = 0
		logs.forEach((it) => {
			if (it.time) total += calculateTimeLogTotal(it, isOvertime)
		})
		return total
	}

	$: fetchModel(id)
	$: totalRegularTime = calculateTotalTime(logs)
	$: totalOvertime = calculateTotalTime(logs, true)
</script>

<Sidepanel>
	<ClientDashSideContent
		{model}
		start={$periodStart}
		end={$periodEnd}
		on:search={onSearch}
		on:type-change={onTypeChange}
		on:period-change={onPeriodChange}
		on:create-log={onCreateLog}
	/>
</Sidepanel>
<AdminLayout classes="sidepanel">
	<div class="avoid-selection">
		{#if screenReady}
			<FadeinFX delay={200}>
				<LogsRenderer
					items={logs}
					start={$periodStart}
					end={$periodEnd}
					on:log-time-update={onTimeLogUpdate}
					on:log-time-create={onTimeLogCreate}
					on:update={onLogUpdate}
					on:cancel={onLogCancel}
					on:delete={onLogDelete}
					on:load-before={loadMoreBefore}
				/>

				<div class="fixed right-4 top-4  text-white">
					<div class="text-base font-semibold bg-darkgray-400 px-3 py-2 rounded-t-md">Periodo visualizzato:</div>
					<div class="bg-darkgray-100 p-3 rounded-b-md">
						<div class="flex items-center gap-4 text-base">
							<div><span class="text-gray-500">Da:</span> {moment($periodStart).format('DD MMM YYYY')}</div>
							<div><span class="text-gray-500">A:</span> {moment($periodEnd).format('DD MMM YYYY')}</div>
						</div>
						<div class="text-darkgray-800 mt-2">
							<div class="">
								<span class="text-gray-300">Ordinari: </span>
								<span class="text-darkgray-900 font-semibold">{totalRegularTime}</span>
							</div>
							<div class="">
								<span class="text-gray-300">Straordinari: </span>
								<span class="text-darkgray-900 font-semibold">{totalOvertime}</span>
							</div>
						</div>
					</div>
				</div>
			</FadeinFX>
		{/if}
	</div>
</AdminLayout>

{#if createLogFlow}
	<CreateLog {id} on:cancel={onCancelCreateLog} on:select={onConfirmCreateLog} />
{/if}
